/**
 * The handful of icons the app needs, inline. No icon library: the whole point is that a
 * cold start with the radio off renders identically to a warm one.
 *
 * Every icon takes `size` and inherits `currentColor`, and is aria-hidden — the label lives
 * on the button, not on the glyph.
 */
import type { ReactNode, SVGProps } from 'react'

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'width' | 'height'> {
  size?: number
}

function svg(path: ReactNode, { size = 22, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {path}
    </svg>
  )
}

export const SearchIcon = (p: IconProps) =>
  svg(
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.6-3.6" />
    </>,
    p,
  )

export const CloseIcon = (p: IconProps) =>
  svg(
    <>
      <path d="M6 6l12 12" />
      <path d="M18 6L6 18" />
    </>,
    p,
  )

export const PlusIcon = (p: IconProps) =>
  svg(
    <>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </>,
    p,
  )

export const MinusIcon = (p: IconProps) => svg(<path d="M5 12h14" />, p)

export const CheckIcon = (p: IconProps) => svg(<path d="M4 12.5l5 5L20 6.5" />, p)

export const ChevronDownIcon = (p: IconProps) => svg(<path d="M5 9l7 7 7-7" />, p)

export const ChevronRightIcon = (p: IconProps) => svg(<path d="M9 5l7 7-7 7" />, p)

export const TrashIcon = (p: IconProps) =>
  svg(
    <>
      <path d="M4 7h16" />
      <path d="M9 7V4h6v3" />
      <path d="M6 7l1 13h10l1-13" />
    </>,
    p,
  )

export const CameraIcon = (p: IconProps) =>
  svg(
    <>
      <path d="M3 8.5h3.5L8 6h8l1.5 2.5H21V19H3z" />
      <circle cx="12" cy="13.5" r="3.5" />
    </>,
    p,
  )

export const BarcodeIcon = (p: IconProps) =>
  svg(
    <>
      <path d="M4 6v12" />
      <path d="M7.5 6v12" />
      <path d="M11 6v12" />
      <path d="M14.5 6v12" />
      <path d="M18 6v12" />
      <path d="M20.5 6v12" />
    </>,
    p,
  )

export const CloudOffIcon = (p: IconProps) =>
  svg(
    <>
      <path d="M17.5 18H7a4 4 0 01-.6-7.95" />
      <path d="M9.2 6.4A5 5 0 0117 9h.5a3.5 3.5 0 012.6 5.8" />
      <path d="M3 3l18 18" />
    </>,
    p,
  )

export const SyncIcon = (p: IconProps) =>
  svg(
    <>
      <path d="M20 11a8 8 0 00-14.1-4.6L4 8" />
      <path d="M4 13a8 8 0 0014.1 4.6L20 16" />
      <path d="M4 4v4h4" />
      <path d="M20 20v-4h-4" />
    </>,
    p,
  )

export const AlertIcon = (p: IconProps) =>
  svg(
    <>
      <path d="M12 4l9 16H3z" />
      <path d="M12 10v4" />
      <path d="M12 17.2v.2" />
    </>,
    p,
  )

export const BasketIcon = (p: IconProps) =>
  svg(
    <>
      <path d="M4 9h16l-1.6 10H5.6z" />
      <path d="M8.5 9l2-5" />
      <path d="M15.5 9l-2-5" />
    </>,
    p,
  )

export const MapIcon = (p: IconProps) =>
  svg(
    <>
      <path d="M3 6l6-2 6 2 6-2v14l-6 2-6-2-6 2z" />
      <path d="M9 4v14" />
      <path d="M15 6v14" />
    </>,
    p,
  )

export const GearIcon = (p: IconProps) =>
  svg(
    <>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 3v2.2M12 18.8V21M4.2 7.5l1.9 1.1M17.9 15.4l1.9 1.1M4.2 16.5l1.9-1.1M17.9 8.6l1.9-1.1" />
    </>,
    p,
  )

/** Brand mark: a ridgeline. Used in the app bar and as the seed for the PWA icons. */
export const RidgeIcon = (p: IconProps) =>
  svg(
    <>
      <path d="M2 18l6.5-10 4 5.5L16 9l6 9z" fill="currentColor" stroke="none" />
      <path d="M2 21h20" />
    </>,
    p,
  )

export const ChevronLeftIcon = (p: IconProps) => svg(<path d="M15 5l-7 7 7 7" />, p)

export const ChevronUpIcon = (p: IconProps) => svg(<path d="M5 15l7-7 7 7" />, p)

/** Recipes: a pot. */
export const PotIcon = (p: IconProps) =>
  svg(
    <>
      <path d="M4 10h16v6a4 4 0 01-4 4H8a4 4 0 01-4-4z" />
      <path d="M2 10h2M20 10h2" />
      <path d="M9 6.5c0-1 1-1.5 1-2.5M13 6.5c0-1 1-1.5 1-2.5" />
    </>,
    p,
  )

/** Foods: an apple-ish leaf and fruit. */
export const FoodIcon = (p: IconProps) =>
  svg(
    <>
      <path d="M12 7c-2-1.6-6-1.2-6.8 2.6C4.4 13.8 7 20 9.6 20c1 0 1.4-.6 2.4-.6s1.4.6 2.4.6c2.6 0 5.2-6.2 4.4-10.4C18 5.8 14 5.4 12 7z" />
      <path d="M12 7c0-2 1-3.4 3-4" />
    </>,
    p,
  )

export const MoreIcon = (p: IconProps) =>
  svg(
    <>
      <circle cx="5" cy="12" r="1.3" fill="currentColor" />
      <circle cx="12" cy="12" r="1.3" fill="currentColor" />
      <circle cx="19" cy="12" r="1.3" fill="currentColor" />
    </>,
    p,
  )

export const EditIcon = (p: IconProps) =>
  svg(
    <>
      <path d="M4 20h4L19 9l-4-4L4 16z" />
      <path d="M13.5 6.5l4 4" />
    </>,
    p,
  )

export const InfoIcon = (p: IconProps) =>
  svg(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5" />
      <path d="M12 7.8v.2" />
    </>,
    p,
  )

export const DownloadIcon = (p: IconProps) =>
  svg(
    <>
      <path d="M12 4v11" />
      <path d="M7 10l5 5 5-5" />
      <path d="M4 20h16" />
    </>,
    p,
  )

export const UploadIcon = (p: IconProps) =>
  svg(
    <>
      <path d="M12 20V9" />
      <path d="M7 14l5-5 5 5" />
      <path d="M4 4h16" />
    </>,
    p,
  )

export const ListIcon = (p: IconProps) =>
  svg(
    <>
      <path d="M9 6h11M9 12h11M9 18h11" />
      <path d="M4.5 6v.01M4.5 12v.01M4.5 18v.01" />
    </>,
    p,
  )

export const GridIcon = (p: IconProps) =>
  svg(
    <>
      <path d="M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z" />
    </>,
    p,
  )

export const UndoIcon = (p: IconProps) =>
  svg(
    <>
      <path d="M9 14L4 9l5-5" />
      <path d="M4 9h10a6 6 0 010 12h-3" />
    </>,
    p,
  )

/** A star; `filled` for a favourite. */
export const StarIcon = ({ filled = false, ...p }: IconProps & { filled?: boolean }) =>
  svg(
    <path
      d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.9l-5.2 2.7 1-5.8L3.5 9.7l5.9-.9z"
      fill={filled ? 'currentColor' : 'none'}
    />,
    p,
  )
